import React from "react";
import { Check, Minus, Sparkles } from "lucide-react";
import { AppTile, BrandCard, TINT_COLORS, tintSurface } from "../integrations/brand";
import { GRATUITO, PER_CHI, alMese, aspettoPiano } from "./piani";

/**
 * La scheda di un piano, la stessa nella pagina Abbonamento e alla fine
 * dell'onboarding: cosi' i piani si presentano uguali per costruzione.
 */

const numero = (n) => Number(n || 0).toLocaleString("it-IT", { maximumFractionDigits: 2 });

/**
 * Piano attivo e piano consigliato, due colori che non si confondono: il
 * verde e' quello di "Attivo" nell'intestazione, il viola pieno invita.
 */
export const BORDO_ATTIVO = "border-[#19D163] shadow-[0_0_0_1px_#19D163]";
export const BORDO_CONSIGLIATO = "border-[#A855F7] shadow-[0_0_0_1px_#A855F7,0_16px_32px_-16px_rgba(168,85,247,0.5)]";
// Il piano scelto nella finestra dei Clienti (assegna piano): scuro come il bottone.
export const BORDO_SCELTO = "border-[#18181B] shadow-[0_0_0_1px_#18181B]";

/**
 * L'alone del colore del piano, come le schede delle app, ma solo al passaggio
 * del mouse (o con il focus dentro): a riposo la scheda e' bianca. Sta dietro
 * al contenuto (-z-10 dentro la scheda `isolate`).
 */
export function AloneHover({ colore }) {
  return (
    <span
      aria-hidden="true"
      className="pointer-events-none absolute inset-0 -z-10 rounded-[14px] opacity-0 transition-opacity duration-300 group-hover:opacity-100 group-focus-within:opacity-100"
      style={{ backgroundImage: tintSurface(colore).backgroundImage }}
    />
  );
}

/** L'etichetta sul bordo alto della scheda (o in linea, nella striscia su misura). */
export function Etichetta({ tipo, inLinea = false }) {
  const attivo = tipo === "attivo";
  const Icona = attivo ? Check : Sparkles;
  return (
    <span
      className={`inline-flex h-6 items-center gap-1 rounded-full px-2.5 text-xs font-semibold ${
        inLinea ? "" : "absolute -top-3 left-6"
      } ${attivo ? "border border-[#19D163] bg-[#F0FDF4] text-[#0F7A3D]" : "bg-[#9333EA] text-white"}`}
    >
      <Icona className="h-3.5 w-3.5" aria-hidden="true" />
      {attivo ? "Il tuo piano" : "Consigliato"}
    </span>
  );
}

/**
 * Un piano: nome e per chi e', il prezzo grande, subito sotto il bottone,
 * poi cosa comprende. Il piano attivo e quello consigliato hanno bordo ed
 * etichetta del loro colore.
 */
export function SchedaPiano({ piano, intervallo, attuale, inEvidenza, azione }) {
  const gratuito = piano.key === GRATUITO;
  const mensile = intervallo === "monthly" && piano.price_monthly > 0;
  const importo = gratuito ? null : mensile ? piano.price_monthly : piano.price_yearly;
  const bordo = attuale ? BORDO_ATTIVO : inEvidenza ? BORDO_CONSIGLIATO : "";
  const aspetto = aspettoPiano(piano.key);
  return (
    <BrandCard className={`group relative isolate flex flex-col p-6 ${bordo}`}>
      <AloneHover colore={TINT_COLORS[aspetto.tint]} />
      {attuale ? <Etichetta tipo="attivo" /> : inEvidenza ? <Etichetta tipo="consigliato" /> : null}
      <AppTile app={aspetto} />
      <h3 className="mt-4 text-base font-semibold text-[#020617]">{piano.name}</h3>
      <p className="mt-2 min-h-[48px] text-sm leading-6 text-[#475569]">{PER_CHI[piano.key] || piano.description}</p>

      <p className="mt-5 flex items-baseline gap-1">
        <span className="text-[32px] font-semibold leading-10 tracking-tight text-[#020617]">
          {gratuito ? "Gratis" : `${numero(importo)} €`}
        </span>
        {/* Si vende alle aziende: ogni prezzo e' IVA esclusa, e lo dice. */}
        {gratuito ? null : <span className="text-sm font-medium text-[#64748B]">{mensile ? "/mese + IVA" : "/anno + IVA"}</span>}
      </p>
      <p className="mt-1 min-h-[20px] text-xs leading-5 text-[#64748B]">
        {gratuito
          ? "Senza carta, per sempre."
          : mensile
            ? "Si rinnova ogni mese."
            : `${numero(alMese(piano.price_yearly))} € al mese + IVA, fatturati una volta l'anno.`}
      </p>

      <div className="mt-5">{azione}</div>

      <div className="mt-6 border-t border-[#F1F5F9] pt-5">
        <Comprende piano={piano} />
      </div>
    </BrandCard>
  );
}

export function Comprende({ piano }) {
  const l = piano.limits || {};
  const f = piano.features || {};
  const quanti = (n, uno, tanti) => (n ? `${numero(n)} ${n === 1 ? uno : tanti}` : `${tanti} illimitati`);
  const voci = [
    { testo: quanti(l.messages_per_month, "risposta al mese", "risposte al mese"), si: true },
    { testo: quanti(l.sources_max, "fonte", "fonti"), si: true },
    { testo: quanti(l.widgets_max, "sito o assistente", "siti o assistenti"), si: true },
    { testo: "WhatsApp", si: Boolean(f.whatsapp_integration) },
    { testo: "Schede prodotto in chat", si: Boolean(f.product_suggestions) },
    { testo: "Senza il logo PingUp", si: f.watermark === false },
  ];
  return (
    <ul className="space-y-2.5 text-sm">
      {voci.map((v) => (
        <li key={v.testo} className={`flex items-start gap-2.5 leading-5 ${v.si ? "text-[#334155]" : "text-[#94A3B8]"}`}>
          {v.si ? (
            <Check className="mt-0.5 h-4 w-4 shrink-0 text-[#0F7A3D]" aria-hidden="true" />
          ) : (
            <Minus className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
          )}
          <span>
            {v.si ? null : <span className="sr-only">Non compreso: </span>}
            {v.testo}
          </span>
        </li>
      ))}
    </ul>
  );
}
